import React, { useEffect, useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import Div100vh from 'react-div-100vh'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import Slider from 'rc-slider'
import { IconButton, Toggle, Tonality } from '@dsny/dsny-component-library'
import {
  AIFilled,
  Download,
  ExplicitFilled,
  Forward10,
  Keyboardarrowdown,
  Keyboardarrowright,
  Morevert,
  PauseFilled,
  PlayArrowFilled,
  Replay10,
  SkipNextFilled,
  SkipPreviousFilled
} from '@dsny/dsny-component-library/icons'
import { albumCovertArtPlaceholder } from 'images'
import { NotificationType } from 'constants/enums'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import ModalActions from 'reducers/Modal'
import PlayerActions from 'reducers/Player'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import NotificationActions from 'reducers/Notification'
import {
  isPlaylist,
  isRelease,
  thunks as PlayQueueThunks
} from 'reducers/PlayQueue'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import { browserHelpers, dateHelpers, stringHelpers } from 'utils'
import { playQueueSelector } from 'components/sidebar/selectors'
import dropdownStyles from 'components/Dropdowns/Dropdowns.module.scss'
import { ScreenReaderText } from './NewPlaybar.styles'
import usePlayerControls from './usePlayerControls'
import {
  AlbumArtLarge,
  AutoplayLabel,
  AutoplayRow,
  EmptyQueueText,
  HeaderSpacer,
  MenuButton,
  PlaybackControls,
  PlayerTimeline,
  QueueArtwork,
  QueueBadges,
  QueueClearButton,
  QueueHeader,
  QueueItemActions,
  QueueItemDuration,
  QueueItemText,
  QueueList,
  QueueArtist,
  QueueMenuButton,
  QueueRow,
  QueueSection,
  QueueTitle,
  QueueTitleRow,
  SheetHeader,
  SheetIconButtonFrame,
  SheetViewport,
  StatusBadge,
  Time,
  TimeRow,
  TrackActionButtons,
  TrackArtist,
  TrackInfo,
  TrackName,
  TrackSummary,
  Wrapper
} from './MobilePlayerSheet.styles'

const DEFAULT_DOWNLOAD_FORMAT = 'mp3'
const PLAYBAR_DOWNLOAD_SOURCE = 'player'

const menuContentClassName = [
  dropdownStyles.DropdownMenuContent,
  dropdownStyles.ReleaseInfoActionsDropdown
].join(' ')

const menuItemClassName = [
  dropdownStyles.DropdownMenuItem,
  dropdownStyles.ReleaseInfoActionsItem
].join(' ')

const getReleaseId = (item) => item?.releaseid || item?.release_id

const getArtwork = (item) =>
  item?.coverart ||
  item?.thumbnail ||
  item?.releaseart ||
  item?.artwork ||
  albumCovertArtPlaceholder

const getQueueDuration = (item) => {
  if (!item?.duration || isRelease(item) || isPlaylist(item)) return ''

  return dateHelpers.getStringFromSeconds(item.duration)
}

export default function MobilePlayerSheet({ toggleExpanded }) {
  const autoPlayToggleId = useId()
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { items, playQueue } = useSelector(playQueueSelector)
  const { playlistAbstracts } = useSelector((state) => state.playList)

  const {
    isPlaying,
    currentTime,
    displayDuration,
    selectedSong,
    isSliderDragging,
    isFirstSong,
    isLastSong,
    handleTogglePlay,
    handleSkipBack,
    handleSkipFwd,
    handleReplayTen,
    handleForwardTen,
    hasNoSong,
    setIsSliderDragging,
    autoPlay,
    handleToggleAutoPlay
  } = usePlayerControls()
  const [sliderCurrentTime, setSliderCurrentTime] = useState(currentTime)
  const coverArt = getArtwork(selectedSong)
  const sliderValue = hasNoSong
    ? 0
    : isSliderDragging
    ? sliderCurrentTime
    : currentTime
  const sliderMax = hasNoSong ? 0 : displayDuration
  const currentTimeText = hasNoSong
    ? '-:--'
    : stringHelpers.formatTime(currentTime)
  const durationText = hasNoSong
    ? '-:--'
    : dateHelpers.getStringFromSeconds(displayDuration)
  const isDownloadDisabled =
    hasNoSong ||
    selectedSong?.candownload === false ||
    !browserHelpers.browserAllowsDownload
  const isPlaylistDisabled = hasNoSong || !selectedSong?.trackid

  const queueItems = useMemo(
    () => items.filter((item) => item.uuid !== selectedSong?.uuid),
    [items, selectedSong?.uuid]
  )

  useEffect(() => {
    if (!isSliderDragging) {
      setSliderCurrentTime(currentTime)
    }
  }, [currentTime, isSliderDragging])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow

    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        toggleExpanded()
      }
    }

    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [toggleExpanded])

  const handleDisabledButtonClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
  }

  const handleSliderChange = (value) => {
    if (hasNoSong) return

    setSliderCurrentTime(value)
    setIsSliderDragging(true)
  }

  const handleSliderRelease = (value) => {
    if (hasNoSong) return

    dispatch(PlayerActions.seekTimeRequest(value))
    setSliderCurrentTime(value)
    setIsSliderDragging(false)
  }

  const handleDownloadTrack = (event) => {
    event.stopPropagation()
    if (isDownloadDisabled) return

    if (selectedSong.assettype === 'video') {
      dispatch(
        DownloadThunks.addVideoToDownloadQueue(
          selectedSong,
          PLAYBAR_DOWNLOAD_SOURCE
        )
      )
      return
    }

    dispatch(
      DownloadThunks.addSongToDownloadQueue(selectedSong, {
        format: DEFAULT_DOWNLOAD_FORMAT,
        source: PLAYBAR_DOWNLOAD_SOURCE
      })
    )
  }

  const handleViewRelease = (event, item = selectedSong) => {
    event?.stopPropagation()

    const releaseId = getReleaseId(item)
    if (!releaseId) return

    toggleExpanded()
    navigate(`/releases/releaseInfo/${releaseId}`)
  }

  const openNewPlaylistModal = (event, track) => {
    event.stopPropagation()
    dispatch(PlaylistActions.setSongsToAdd([track.trackid]))
    dispatch(ModalActions.togglePlaylistModal())
  }

  const handleAddToPlaylist = (event, playlist, track) => {
    event.stopPropagation()
    dispatch(
      PlaylistThunks.addTracks(playlist.playlistid, [track.trackid], () =>
        dispatch(
          NotificationActions.broadcastMessage(
            {
              songTitle: track.title,
              playlistTitle: playlist.name
            },
            NotificationType.addedToPlaylist
          )
        )
      )
    )
  }

  const handlePlayQueueItem = (item) => {
    dispatch(
      PlayQueueThunks.seekToItem(item, {
        source: PLAY_SOURCES.QUEUE,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  const handleQueueItemKeyDown = (event, item) => {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    handlePlayQueueItem(item)
  }

  const handleRemoveQueueItem = (event, item) => {
    event.stopPropagation()
    dispatch(PlayQueueThunks.deleteItem(item, playQueue, isPlaying))
  }

  const openClearQueueModal = () => {
    dispatch(ModalActions.toggleClearQueueModal())
  }

  const renderIconButton = ({
    label,
    Icon,
    onClick,
    disabled = false,
    primary = false,
    iconSize = 24,
    size = 40
  }) => (
    <SheetIconButtonFrame
      $disabled={disabled}
      $primary={primary}
      $size={size}
    >
      <IconButton onClick={disabled ? handleDisabledButtonClick : onClick}>
        <Icon
          aria-hidden="true"
          color="currentColor"
          height={iconSize}
          width={iconSize}
        />
        <ScreenReaderText>{label}</ScreenReaderText>
      </IconButton>
    </SheetIconButtonFrame>
  )

  const renderTooltip = (content, children) => {
    if (!content) return children

    return (
      <Tonality.Tooltip
        mode="light"
        content={content}
        containerStyles={{ padding: '4px 8px' }}
      >
        {children}
      </Tonality.Tooltip>
    )
  }

  const renderPlaylistSubMenu = (track) => {
    if (isPlaylistDisabled) return null

    return (
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger className={menuItemClassName}>
          {t('playlist.add-to-playlist')}
          <div className={dropdownStyles.RightSlot}>
            <Keyboardarrowright
              color={theme.colors.neutralW100}
              height={16}
              width={16}
            />
          </div>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent
            avoidCollisions
            alignOffset={-5}
            className={menuContentClassName}
            sideOffset={2}
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100
            }}
          >
            {playlistAbstracts?.map((playlist) => (
              <DropdownMenu.Item
                key={playlist?.playlistid}
                className={menuItemClassName}
                onClick={(event) =>
                  handleAddToPlaylist(event, playlist, track)
                }
              >
                {playlist?.name}
              </DropdownMenu.Item>
            ))}
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={(event) => openNewPlaylistModal(event, track)}
            >
              {t('playlist.new-playlist')}
            </DropdownMenu.Item>
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
    )
  }

  const renderTrackMoreMenu = () => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild disabled={hasNoSong}>
        <MenuButton
          aria-label={t('general.more-actions')}
          disabled={hasNoSong}
          type="button"
        >
          <Morevert
            aria-hidden="true"
            color="currentColor"
            height={24}
            width={24}
          />
        </MenuButton>
      </DropdownMenu.Trigger>
      {!hasNoSong && (
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            avoidCollisions
            align="end"
            className={menuContentClassName}
            side="top"
            sideOffset={8}
            style={{
              background: theme.colors.neutralW20,
              color: theme.colors.neutralW100
            }}
          >
            {renderPlaylistSubMenu(selectedSong)}
            {getReleaseId(selectedSong) && (
              <DropdownMenu.Item
                className={menuItemClassName}
                onClick={(event) => handleViewRelease(event)}
              >
                {t('general.view-release')}
              </DropdownMenu.Item>
            )}
            <DropdownMenu.Arrow
              className={dropdownStyles.DropdownMenuArrow}
              style={{ fill: theme.colors.neutralW20 }}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </DropdownMenu.Root>
  )

  const renderQueueItemMenu = (item) => (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <QueueMenuButton
          aria-label={`${t('general.more-actions')} ${item?.title || ''}`}
          type="button"
          onClick={(event) => event.stopPropagation()}
        >
          <Morevert
            aria-hidden="true"
            color="currentColor"
            height={20}
            width={20}
          />
        </QueueMenuButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          avoidCollisions
          align="end"
          className={menuContentClassName}
          side="top"
          sideOffset={8}
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100
          }}
        >
          <DropdownMenu.Item
            className={menuItemClassName}
            onClick={(event) => handleRemoveQueueItem(event, item)}
          >
            {t('general.remove-from-queue')}
          </DropdownMenu.Item>
          {getReleaseId(item) && (
            <DropdownMenu.Item
              className={menuItemClassName}
              onClick={(event) => handleViewRelease(event, item)}
            >
              {t('general.view-release')}
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Arrow
            className={dropdownStyles.DropdownMenuArrow}
            style={{ fill: theme.colors.neutralW20 }}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )

  const renderQueueItem = (item) => {
    const title = item?.title || item?.name || t('sidebar.play-queue')
    const artist = item?.artist || item?.message || ''
    const duration = getQueueDuration(item)
    const hasBadges =
      item?.explicit || item?.is_ai_generated || item?.assettype === 'video'

    return (
      <QueueRow
        key={item.uuid || item.trackid || item.releaseid || item.playlistid}
        role="button"
        tabIndex={0}
        onClick={() => handlePlayQueueItem(item)}
        onKeyDown={(event) => handleQueueItemKeyDown(event, item)}
      >
        <QueueArtwork alt="" src={getArtwork(item)} />
        <QueueItemText>
          <QueueTitleRow>
            {hasBadges && (
              <QueueBadges>
                {item?.explicit && (
                  <ExplicitFilled
                    color={theme.colors.neutralW80}
                    height={16}
                    width={16}
                  />
                )}
                {item?.is_ai_generated && (
                  <AIFilled
                    color={theme.colors.neutralW80}
                    height={12}
                    width={12}
                  />
                )}
                {item?.assettype === 'video' && (
                  <StatusBadge>{item.assettype}</StatusBadge>
                )}
              </QueueBadges>
            )}
            {renderTooltip(title, <QueueTitle>{title}</QueueTitle>)}
          </QueueTitleRow>
          {renderTooltip(artist, <QueueArtist>{artist}</QueueArtist>)}
        </QueueItemText>
        <QueueItemDuration>{duration}</QueueItemDuration>
        <QueueItemActions>{renderQueueItemMenu(item)}</QueueItemActions>
      </QueueRow>
    )
  }

  return createPortal(
    <Wrapper
      animate={{ opacity: 1, y: 0 }}
      initial={{ opacity: 0, y: 80 }}
      transition={{ damping: 22, stiffness: 180, type: 'spring' }}
    >
      <Div100vh>
        <SheetViewport
          aria-label="Mobile player"
          aria-modal="true"
          role="dialog"
        >
          <SheetHeader>
            <HeaderSpacer />
            <MenuButton
              aria-label="Collapse player"
              type="button"
              onClick={toggleExpanded}
            >
              <Keyboardarrowdown
                aria-hidden="true"
                color="currentColor"
                height={24}
                width={24}
              />
            </MenuButton>
          </SheetHeader>

          <AlbumArtLarge
            alt={selectedSong?.title || 'Album art'}
            src={coverArt}
          />

          <TrackSummary>
            <TrackInfo>
              <TrackName>{hasNoSong ? '' : selectedSong?.title}</TrackName>
              <TrackArtist>{hasNoSong ? '' : selectedSong?.artist}</TrackArtist>
            </TrackInfo>
            <TrackActionButtons>
              {renderIconButton({
                label: t('general.download-this-song'),
                Icon: Download,
                onClick: handleDownloadTrack,
                disabled: isDownloadDisabled
              })}
              {renderTrackMoreMenu()}
            </TrackActionButtons>
          </TrackSummary>

          <PlayerTimeline>
            <Slider
              className="cursor-pointer"
              defaultValue={0}
              disabled={hasNoSong}
              handleStyle={{
                backgroundColor: theme.colors.neutralW100,
                borderColor: theme.colors.neutralW100,
                boxShadow: 'none',
                height: 12,
                width: 12
              }}
              max={sliderMax}
              min={0}
              railStyle={{
                backgroundColor: theme.colors.neutralW30
              }}
              step={0.01}
              trackStyle={{
                backgroundColor: theme.colors.player50
              }}
              value={sliderValue}
              onAfterChange={handleSliderRelease}
              onChange={handleSliderChange}
            />
          </PlayerTimeline>
          <TimeRow>
            <Time>{currentTimeText}</Time>
            <Time>{durationText}</Time>
          </TimeRow>

          <PlaybackControls>
            {renderIconButton({
              label: 'Previous track',
              Icon: SkipPreviousFilled,
              onClick: handleSkipBack,
              disabled: hasNoSong || isFirstSong,
              iconSize: 21,
              size: 38
            })}
            {renderIconButton({
              label: 'Back 10 seconds',
              Icon: Replay10,
              onClick: handleReplayTen,
              disabled: hasNoSong,
              iconSize: 21,
              size: 38
            })}
            {renderIconButton({
              label: isPlaying ? 'Pause' : t('general.play'),
              Icon: isPlaying && !hasNoSong ? PauseFilled : PlayArrowFilled,
              onClick: handleTogglePlay,
              disabled: hasNoSong,
              primary: true,
              iconSize: 24,
              size: 40
            })}
            {renderIconButton({
              label: 'Forward 10 seconds',
              Icon: Forward10,
              onClick: handleForwardTen,
              disabled: hasNoSong,
              iconSize: 21,
              size: 38
            })}
            {renderIconButton({
              label: 'Next track',
              Icon: SkipNextFilled,
              onClick: handleSkipFwd,
              disabled: hasNoSong || isLastSong,
              iconSize: 21,
              size: 38
            })}
          </PlaybackControls>

          <QueueSection>
            <QueueHeader>
              <span>{t('sidebar.queue')}</span>
              {queueItems.length > 0 && (
                <QueueClearButton type="button" onClick={openClearQueueModal}>
                  {t('home.clear-button-text')}
                </QueueClearButton>
              )}
            </QueueHeader>
            {queueItems.length > 0 ? (
              <QueueList>{queueItems.map(renderQueueItem)}</QueueList>
            ) : (
              <EmptyQueueText>{t('sidebar.empty-queue-message')}</EmptyQueueText>
            )}
          </QueueSection>

          <AutoplayRow>
            <AutoplayLabel>{t('general.autoplay')}</AutoplayLabel>
            <Toggle
              checked={autoPlay}
              disabled={hasNoSong}
              displayIcon={true}
              id={autoPlayToggleId}
              player={true}
              onChange={handleToggleAutoPlay}
            />
          </AutoplayRow>
        </SheetViewport>
      </Div100vh>
    </Wrapper>,
    document.body
  )
}
